import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Box, Button, Grid, LinearProgress, Stack, Typography, useTheme } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { EmptyState, PageHeader, Pill, SectionCard } from "../components/ui";
import { api } from "../api/axios";

export default function AIAssistant() {
  const { id } = useParams();
  const theme = useTheme();
  const BackIcon = theme.direction === "rtl" ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  const [credits, setCredits] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, j] = await Promise.all([
      api.get("/api/ai/credits"),
      api.get("/api/ai/jobs", { params: { siteId: id } }),
    ]);
    setCredits(c.data.credits);
    setJobs(j.data.jobs || []);
  }, [id]);

  useEffect(() => {
    load().catch((err) => setError(err?.response?.data?.message || "שגיאה"));
  }, [load]);

  const runAudit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/ai/jobs", { siteId: id, type: "audit", input: { mode: "mvp" } });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה");
    } finally {
      setBusy(false);
    }
  };

  const jobTone = (st) => (st === "done" || st === "completed" ? "success" : st === "failed" || st === "error" ? "error" : st === "running" ? "info" : "neutral");
  const usedPct = credits && Number(credits.granted) > 0 ? Math.min(100, (Number(credits.used || 0) / Number(credits.granted)) * 100) : 0;

  return (
    <Box>
      <Button component={RouterLink} to={`/sites/${id}`} size="small" startIcon={<BackIcon />} sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}>
        חזרה לאתר
      </Button>
      <PageHeader
        title="AI Audit (MVP)"
        subtitle="ה‑Audit ינסה למשוך את דף הבית של הדומיין (לפי מה שמוגדר ב‑Site) ולעדכן אוטומטית חלק מהבדיקות ב‑Compliance Center (ALT/lang/headings/labels)."
        actions={
          <Button variant="contained" onClick={runAudit} disabled={busy} startIcon={<PlayArrowRoundedIcon />}>
            הרץ בדיקה (Audit)
          </Button>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <SectionCard title="קרדיטים החודש" icon={<BoltOutlinedIcon />} iconTone="warning">
            <Typography sx={{ fontSize: "1.9rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
              {credits ? `${credits.used}/${credits.granted}` : "…"}
            </Typography>
            <LinearProgress variant="determinate" value={usedPct} sx={{ mt: 1.5 }} aria-label={`ניצול קרדיטים ${Math.round(usedPct)}%`} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={8}>
          <SectionCard title="Jobs אחרונים" icon={<HistoryRoundedIcon />} iconTone="violet" contentSx={{ p: 0 }}>
            {jobs.length === 0 ? (
              <EmptyState sx={{ py: 4 }} icon={<AutoAwesomeOutlinedIcon />} title="אין jobs עדיין." description="הריצו בדיקה ראשונה כדי לראות כאן את התוצאות." />
            ) : (
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
                {jobs.slice(0, 20).map((j) => (
                  <Stack component="li" key={j.id} direction="row" spacing={1.5} alignItems="center" sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                      #{j.id} · {j.type}
                    </Typography>
                    <Typography variant="caption">cost: {j.credits_cost}</Typography>
                    <Pill tone={jobTone(j.status)}>{j.status}</Pill>
                  </Stack>
                ))}
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
